/**
 * Which planet the world shows, and how big: the welcome planet turning by itself ('demo') or the
 * game's planet ('game'). The radius eases from one planet's size to the next.
 */
import { makeGrid, type Grid } from '@bramvasterd/terrella-rules';

import type { Game } from '@game/game';

import type { Props } from '../props';
import { showcasePlanet } from '../showcase/showcase';
import { planetViewOf } from './gameView';
import type { Planet } from './planet';
import { radiusOf, type PlanetView } from './view';

export type Mode = 'demo' | 'game';

export interface Display {
  readonly mode: Mode;
  /** The grid shown; null before anything is. */
  readonly grid: Grid | null;
  /** The radius of the planet shown. */
  readonly radius: number;
  /** The radius it is drawn at now, easing towards `radius`. */
  readonly shownRadius: number;
  /** The game's planet as drawn (its tiles and machines), while the game is shown. */
  readonly game: PlanetView | null;
  showDemo(now: number): void;
  /** The game's planet, from the store's view; `jump` skips the easing to its size. */
  showGame(now: number, jump: boolean): void;
  /** Show any planet; returns the changed tiles (null: all). */
  show(view: PlanetView, now: number, jump: boolean): number[] | null;
  /** Ease the drawn radius towards the planet's; returns whether it is still easing. */
  ease(dt: number, reduced: boolean): boolean;
  /** The grid of a planet size, made once. */
  gridOf(subdivisions: number): Grid;
}

/** Which machines stand where, as one string to compare. */
function extractorsOn(view: PlanetView): string {
  let out = '';
  for (const [tile, e] of view.machines ?? [])
    out += `${String(tile)}:${e.type}:${String(e.level)};`;
  return out;
}

export function createDisplay(game: Game, planet: Planet, props: Props): Display {
  const grids = new Map<number, Grid>();
  let mode: Mode = 'demo';
  let grid: Grid | null = null;
  let radius = radiusOf(3);
  let shownRadius = radius;
  let shownGame: PlanetView | null = null;
  let demo: PlanetView | null = null;
  /** Where the extractors stood in the last view shown, and what they were. */
  let extractors = '';

  const gridOf = (subdivisions: number): Grid => {
    let g = grids.get(subdivisions);
    if (!g) {
      g = makeGrid(subdivisions);
      grids.set(subdivisions, g);
    }
    return g;
  };

  const show = (view: PlanetView, now: number, jump: boolean): number[] | null => {
    const changed = planet.show(view, now);
    // an extractor placed where all its ground glowed already changes no tile, but needs drawing
    const standing = extractorsOn(view);
    if (changed === null || changed.length > 0 || standing !== extractors)
      props.build(view, mode === 'demo' ? 'low' : 'full');
    else props.track(view);
    extractors = standing;
    grid = view.grid;
    radius = radiusOf(grid.subdivisions);
    if (jump) shownRadius = radius;
    return changed;
  };

  return {
    get mode() {
      return mode;
    },
    get grid() {
      return grid;
    },
    get radius() {
      return radius;
    },
    get shownRadius() {
      return shownRadius;
    },
    get game() {
      return mode === 'game' ? shownGame : null;
    },
    showDemo(now) {
      mode = 'demo';
      demo ??= showcasePlanet();
      show(demo, now, true);
    },
    showGame(now, jump) {
      const s = game.store.get();
      if (!s.view || !s.groups) return;
      mode = 'game';
      shownGame = planetViewOf(s.view, s.groups, gridOf(s.view.subdivisions));
      show(shownGame, now, jump);
    },
    show(view, now, jump) {
      return show(view, now, jump);
    },
    ease(dt, reduced) {
      if (Math.abs(shownRadius - radius) <= 0.001) {
        shownRadius = radius;
        return false;
      }
      shownRadius += (radius - shownRadius) * (reduced ? 1 : Math.min(1, dt * 2.2));
      return true;
    },
    gridOf,
  };
}
